import { Typography, Box } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import { TutorialMessage } from "./TutorialComponents";
import "./index.css";
import { SectionProps } from "./types";
import Load_PNG from "./images/polymer/load.png";
import Peptide_GIF from "./images/polymer/peptide.gif";
import IsoPeptide_GIF from "./images/polymer/isopeptidique_link.gif";
import Peg_GIF from "./images/polymer/peg.gif";
import { ImageMagnify } from "src/Shared";
const useStyles = makeStyles({
  ul: {
    listStyle: "none",
    padding: 0,
    margin: 0,
  },
  li: {
    display: "flex",
    alignItems: "center",
    marginBottom: 8,
    whiteSpace: "nowrap",
  },
  icon: {
    marginRight: 8,
    fontSize: 18,
  },
});

const Polymer = (props: SectionProps) => {
  const classes = useStyles();
  return (
    <div className="container">
      <Typography variant="h2">Polymer Builder</Typography>

      <Typography
        variant="body1"
        style={{ marginTop: "2em", textAlign: "justify" }}
        component="div"
      >
        The <span className="MAD_logo">MAD:Polymer Editor</span> is the latest
        major extension to the MAD webserver. It acts as a graphical user
        interface and workflow manager for the{" "}
        <a href="https://github.com/marrink-lab/polyply_1.0" target="_blank" rel="noreferrer">
          polyply
        </a>
        [<a href="#references">1</a>] package, assisting users in generating
        input files and initial coordinates for the simulation of disordered
        (bio)macromolecules such as{" "}
        <span style={{ fontWeight: "bold" }}>
          synthetic polymers, polypeptides, or polysaccharides
        </span>
        . Molecules are represented as <b>graphs</b>: nodes are building blocks
        (single beads, residues, or larger fragments) and edges follow the
        molecule's covalent connectivity. Building blocks can be selected from
        the library shipped with polyply, expanded with all molecules registered
        in the <a href="/tutorials#database">MAD:Database</a>, and connected
        interactively in the molecule sketcher. From this graph, the simulation
        input files are generated and exported as standard GROMACS files
        compatible with the Martini 3 force field.
      </Typography>

      <div id="summary">
        <Typography
          variant="body1"
          component="div"
          style={{ marginTop: "0.5em" }}
        >
          To illustrate the capabilities and flexibility of the{" "}
          <span className="MAD_logo">MAD:Polymer Editor</span>, the following
          case studies walk through a set of examples of increasing
          architectural complexity, comprising polysaccharides, intrinsically
          disordered proteins (IDP) and synthetic polymers.
        </Typography>
        <Box style={{ marginBottom: "0.5em" }}>
          {" "}
          <a href="#dextran"> 1. A linear polysaccharide: dextran </a>{" "}
        </Box>
        <Box style={{ marginBottom: "0.5em" }}>
          {" "}
          <a href="#idp">
            {" "}
            2. A biological disordered polymer: the Sic1 IDP{" "}
          </a>{" "}
        </Box>
        <Box style={{ marginBottom: "0.5em" }}>
          {" "}
          <a href="#copolymer">
            3. A synthetic block copolymer: PS–PMA{" "}
          </a>{" "}
        </Box>
        <Box style={{ marginBottom: "0.5em" }}>
          <a href="#peg_lipid">
            4. A branched architecture: a TWEEN-like PEG–lipid{" "}
          </a>
        </Box>
      </div>

      <div className="disclaimer" style={{ width: "75%", marginBottom: "2em" }}>
        <div>
          <i
            className="medium material-icons inline-icon"
            style={{ fontSize: "3em" }}
          >
            account_tree
          </i>
        </div>
        <Typography component="div">
          The structures below were built using the interactive graph-based{" "}
          <span className="MAD_logo">MAD:Polymer Editor</span>, with some
          examples combining building blocks originating from polyply and
          retrieved from the <a href="/tutorials#database">MAD:Database</a>. The
          resulting models were exported as standard GROMACS input files and
          tested for compatibility with Martini 3 simulation protocols.
        </Typography>
      </div>

      <div id="dextran">
        <Box
          style={{
            display: "flex",
            flexDirection: "row",
            gap: "4em",
            marginTop: "2em",
            marginBottom: "4em",
          }}
        >
          <Box
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-around",
              gap: "2em",
            }}
          >
            <Typography variant="h4" component="div">
              A linear polysaccharide: dextran
            </Typography>
            <Typography variant="body1" component="div">
              A dextran polymer can be constructed by repeating a glucose-based
              monomer taken from the polyply library of the{" "}
              <span className="MAD_logo">MAD:Polymer Editor</span>. Dextran is a
              polysaccharide composed of α-1,6–linked glucose units, well known
              for its high water solubility compared to other polyglucoses.
            </Typography>
            <Box>
              <ul className={classes.ul}>
                <li className={classes.li}>
                  <ArrowForwardIcon />
                  <span className="li_supt" style={{ marginRight: "0.5em" }}>
                    Predefined building block:{" "}
                  </span>{" "}
                  a single glucose monomer from the polyply library.
                </li>
                <li className={classes.li}>
                  <ArrowForwardIcon />{" "}
                  <span className="li_supt" style={{ marginRight: "0.5em" }}>
                    Linear repetition:
                  </span>{" "}
                  the monomer is added as linearly connected copies to form the
                  chain.
                </li>
                <li className={classes.li}>
                  <ArrowForwardIcon />{" "}
                  <span className="li_supt" style={{ marginRight: "0.5em" }}>
                    Carbohydrate model:
                  </span>{" "}
                  parameters follow the Martini 3 carbohydrate model[
                  <a href="#references">2</a>].
                </li>
              </ul>
            </Box>
            <Box style={{ maxWidth: "80%" }}>
              <TutorialMessage
                type="info"
                message={
                  <>
                    While high–molecular weight dextrans are typically branched,
                    lower degrees of polymerization are often only weakly
                    branched or effectively linear. The model built here is a{" "}
                    <b>linear chain of 20 glucose monomers</b>, illustrating the
                    straightforward construction of carbohydrate-based polymers
                    from predefined building blocks.
                  </>
                }
              />
            </Box>
          </Box>
          <Box style={{ flex: 1 }}>
            <ImageMagnify
              src={Load_PNG}
              alt="dextran_building_blocks"
              width={90}
              preWidth="450px"
            />
          </Box>
        </Box>
      </div>

      <div id="idp">
        <Box
          style={{
            display: "flex",
            flexDirection: "row",
            gap: "4em",
            marginTop: "2em",
            marginBottom: "4em",
          }}
        >
          <Box
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-around",
              gap: "2em",
            }}
          >
            <Typography variant="h4" component="div">
              A biological disordered polymer: the Sic1 IDP
            </Typography>
            <Typography variant="body1" component="div">
              The same workflow applies to the generation of a sequence-defined
              disordered protein. As an example of a biological polymer, we use
              the intrinsically disordered cyclin-dependent kinase inhibitor{" "}
              <b>Sic1</b> from <i>Saccharomyces cerevisiae</i>, a prototypical
              IDP that has been extensively studied experimentally and
              computationally.
            </Typography>
            <Box>
              <ul className={classes.ul}>
                <li className={classes.li}>
                  <ArrowForwardIcon />
                  <span className="li_supt" style={{ marginRight: "0.5em" }}>
                    Sequence-driven:{" "}
                  </span>{" "}
                  a truncated construct (residues 1–90) is generated directly
                  from a FASTA sequence.
                </li>
                <li className={classes.li}>
                  <ArrowForwardIcon />{" "}
                  <span className="li_supt" style={{ marginRight: "0.5em" }}>
                    Martini3-IDP model:
                  </span>{" "}
                  the construct leverages the Martini3-IDP extension[
                  <a href="#references">3</a>] for disordered protein regions.
                </li>
                <li className={classes.li}>
                  <ArrowForwardIcon />{" "}
                  <span className="li_supt" style={{ marginRight: "0.5em" }}>
                    No structure required:
                  </span>{" "}
                  no predefined atomistic structure is needed to build the
                  model.
                </li>
              </ul>
            </Box>
            <Box style={{ maxWidth: "80%" }}>
              <TutorialMessage
                type="info"
                message={
                  <>
                    This example highlights how the{" "}
                    <span className="MAD_logo">MAD:Polymer Editor</span> can be
                    used for sequence-driven, intrinsically disordered
                    biomolecular systems.
                  </>
                }
              />
            </Box>
          </Box>
          <Box style={{ flex: 1 }}>
            <ImageMagnify
              src={Peptide_GIF}
              alt="sic1_idp_construction"
              width={90}
              preWidth="450px"
            />
          </Box>
        </Box>
      </div>

      <div id="copolymer">
        <Box
          style={{
            display: "flex",
            flexDirection: "row",
            gap: "4em",
            marginTop: "2em",
            marginBottom: "4em",
          }}
        >
          <Box
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-around",
              gap: "2em",
            }}
          >
            <Typography variant="h4" component="div">
              A synthetic block copolymer: PS–PMA
            </Typography>
            <Typography variant="body1" component="div">
              To demonstrate the assembly of heterogeneous synthetic polymer
              architectures, an amphiphilic <b>block copolymer</b> composed of
              polystyrene (PS) and polymethyl acrylate (PMA) can be constructed
              with the same graph-based approach used for dextran.
            </Typography>
            <Box>
              <ul className={classes.ul}>
                <li className={classes.li}>
                  <ArrowForwardIcon />
                  <span className="li_supt" style={{ marginRight: "0.5em" }}>
                    Two blocks:{" "}
                  </span>{" "}
                  building blocks for each polymer segment with 20 monomers
                  (PS20–PMA20) are selected and connected interactively.
                </li>
                <li className={classes.li}>
                  <ArrowForwardIcon />{" "}
                  <span className="li_supt" style={{ marginRight: "0.5em" }}>
                    Missing bond detection:
                  </span>{" "}
                  at the junction between the two blocks, a missing bond is
                  detected and highlighted by the interface.
                </li>
                <li className={classes.li}>
                  <ArrowForwardIcon />{" "}
                  <span className="li_supt" style={{ marginRight: "0.5em" }}>
                    Bond editing:
                  </span>{" "}
                  the missing bond is resolved using the bond editing tools
                  provided by the editor.
                </li>
              </ul>
            </Box>
            <Box style={{ maxWidth: "80%" }}>
              <TutorialMessage
                type="info"
                message={
                  <>
                    This example shows how chemically distinct polymer blocks of
                    user-defined lengths can be combined within a single
                    workflow and exported as simulation-ready topologies
                    compatible with the Martini 3 force field.
                  </>
                }
              />
            </Box>
          </Box>
          <Box style={{ flex: 1 }}>
            <ImageMagnify
              src={IsoPeptide_GIF}
              alt="copolymer_bond_editing"
              width={90}
              preWidth="450px"
            />
          </Box>
        </Box>
      </div>

      <div id="peg_lipid">
        <Box
          style={{
            display: "flex",
            flexDirection: "row",
            gap: "4em",
            marginTop: "2em",
            marginBottom: "4em",
          }}
        >
          <Box
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-around",
              gap: "2em",
            }}
          >
            <Typography variant="h4" component="div">
              A branched architecture: a TWEEN-like PEG–lipid
            </Typography>
            <Typography variant="body1" component="div">
              Finally, a branched polyethylene glycol (PEG)-based lipid
              illustrates cases where nontrivial connectivity and building
              blocks lead to missing parameters. The architecture is inspired by
              PEG–lipid surfactants such as polysorbates (e.g., TWEEN-like
              molecules)[<a href="#references">4</a>], which combine branched PEG
              chains with hydrophobic moieties.
            </Typography>
            <Box>
              <ul className={classes.ul}>
                <li className={classes.li}>
                  <ArrowForwardIcon />
                  <span className="li_supt" style={{ marginRight: "0.5em" }}>
                    Combining sources:{" "}
                  </span>{" "}
                  a tetrahydrofuran moiety and oleic acid taken from the{" "}
                  <a href="/tutorials#database">MAD:Database</a> are incorporated
                  into the PEG-branched architecture.
                </li>
                <li className={classes.li}>
                  <ArrowForwardIcon />{" "}
                  <span className="li_supt" style={{ marginRight: "0.5em" }}>
                    Automatic detection:
                  </span>{" "}
                  undefined bonds involving the tetrahydrofuran group, the PEG
                  branches and the oleic acid are automatically detected and
                  highlighted.
                </li>
                <li className={classes.li}>
                  <ArrowForwardIcon />{" "}
                  <span className="li_supt" style={{ marginRight: "0.5em" }}>
                    Interactive resolution:
                  </span>{" "}
                  these connections are resolved interactively with default
                  parameters as placeholders, and the fatty acid head bead type
                  is changed to a SN4a given its connectivity with the PEG
                  moiety.
                </li>
              </ul>
            </Box>
            <Box style={{ maxWidth: "80%" }}>
              <TutorialMessage
                type="info"
                message={
                  <>
                    This example illustrates how polymer models generated with
                    the <span className="MAD_logo">MAD:Polymer Editor</span> can
                    be combined with molecules from the{" "}
                    <a href="/tutorials#database">MAD:Database</a>.
                  </>
                }
              />
            </Box>
          </Box>
          <Box style={{ flex: 1 }}>
            <ImageMagnify
              src={Peg_GIF}
              alt="peg_lipid_construction"
              width={90}
              preWidth="450px"
            />
          </Box>
        </Box>
      </div>

      <div id="export">
        <Typography variant="h4">Visualizing and exporting</Typography>
        <Box
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "2em",
            marginTop: "2em",
            marginBottom: "4em",
          }}
        >
          <Typography component="div" variant="body1">
            For all the examples above, the generated macromolecular models can
            be visualized directly within the MAD web interface of the{" "}
            <a href="/tutorials#molecule">MAD:Molecule Builder</a> and exported
            as complete topology and coordinate files. The systems can also be
            transferred to the{" "}
            <a href="/tutorials#system">MAD:System Builder</a> and solvated in
            water boxes. Short CG simulations confirmed that the resulting
            systems are stable and suitable for use in standard Martini 3
            simulation workflows.
          </Typography>
          <Box style={{ maxWidth: "75%" }}>
            <TutorialMessage
              type="todo"
              message={
                <>
                  Open the{" "}
                  <a href="https://mad.ens-lyon.fr/polymer" target="_blank" rel="noreferrer">
                    MAD:Polymer Editor
                  </a>{" "}
                  and try building one of the architectures above, then export
                  your model with the <b>DOWNLOAD</b> link.
                </>
              }
            />
          </Box>
        </Box>
      </div>

      <div id="references">
        <Typography variant="h4">References</Typography>
        <ol>
          <li>
            Grünewald F., Alessandri R., Kroon P.C., Monticelli L., Souza
            P.C.T., Marrink S.J. Polyply; a python suite for facilitating
            simulations of macromolecules and nanomaterials. Nat. Commun. (2022)
            13, 68.
          </li>
          <li>
            Grünewald F., Punt M.H., Jefferys E.E. et al. Martini 3
            Coarse-Grained Force Field for Carbohydrates. J. Chem. Theory
            Comput. (2022) 18, 7555–7569.
          </li>
          <li>
            Wang L., Brasnett C., Borges-Araújo L., Souza P.C.T., Marrink S.J.
            Martini3-IDP: improved Martini 3 force field for disordered
            proteins. Nature Communications (2025) 16, 2874.
          </li>
          <li>
            Lou H., Wu Y., Kuczera K., Schöneich C. Coarse-Grained Molecular
            Dynamics Simulation of Heterogeneous Polysorbate 80 Surfactants and
            their Interactions with Small Molecules and Proteins. Molecular
            Pharmaceutics (2024) 21, 5041–5052.
          </li>
        </ol>
      </div>
    </div>
  );
};
export default Polymer;
